import { DetailedPages, DetailPagesTypes } from "../types";
import { ButtonType } from "../../molecules/types";
import { BackLinkStyle } from "./types";

type PageWrapperInnerData = { title: string; slideOverDeleteMessage?: string };

// Must list every DetailedPages member — newPagesData below is cast to PageWrapperData
// (a Record over all of them), so a partial list here would silently make some of that
// cast's entries undefined at runtime despite the type claiming otherwise. "service" and
// "boosting" are unreachable in practice (the merged campaign-creation flow always creates
// with entity "campaign" now), but stay here for that reason rather than being dropped.
const detailPages: DetailedPages[] = [
  "site",
  "company",
  "user",
  "dashboard",
  "campaign",
  "service",
  "boosting",
];

type PageWrapperData = {
  [key in DetailedPages]: PageWrapperInnerData;
};

const newPageData = (pageTitle: DetailedPages): PageWrapperInnerData => ({
  title: `Create new ${pageTitle}`,
});

const newPagesData = Object.fromEntries(
  detailPages.map((page): [DetailedPages, PageWrapperInnerData] => [
    page,
    newPageData(page),
  ]),
) as PageWrapperData;

export const actionDataFromEntity = (
  entity: DetailedPages | "product" | "image",
  isNewPage = false,
) => {
  const isCampaign = entity.includes("service") || entity.includes("boosting");
  const relevantPermission = isCampaign ? "campaign" : entity;
  // entity "campaign" (the merged create flow) already reads correctly as its own
  // relevantTitle without the "X campaign" suffix isCampaign would otherwise add.
  const relevantTitle = isCampaign ? `${entity} campaign` : entity;

  return {
    actionPermissions: `${relevantPermission}:${relevantPermission}:${isNewPage ? "create" : "delete"}`, //no permissions required and therefore imported for product and image
    actionText: `${isNewPage ? "Create" : "Delete"} ${relevantTitle}`,
    actionModalMessage: isNewPage
      ? ""
      : `Do you want to delete ${relevantTitle}?`,
  };
};

export const pageWrapperDataByPageType: {
  [key in DetailPagesTypes]: PageWrapperData;
} = {
  new: newPagesData,
  detail: {
    service: {
      title: "Campaign Details", //TODO - when boosting will be enabled for all - name it service
      slideOverDeleteMessage:
        "Widgets relying on this campaign will be affected.", //TODO - when boosting will be enabled for all - name it service
    },
    boosting: {
      title: "Boosting Campaign Details",
      slideOverDeleteMessage:
        "Service campaigns relying on this boosting campaign will be affected.",
    },
    // Unreachable today: detail pages still pass "service" or "boosting" explicitly
    // (routes/campaign/service/details.tsx, routes/campaign/boosting/details.tsx) — this
    // entry exists only because "campaign" joining DetailedPages requires every key here.
    campaign: {
      title: "Campaign Details",
    },
    site: {
      title: "Site Details",
      slideOverDeleteMessage:
        "All related entities will be deleted. This includes campaigns of this site, uploaded files, site product feed and feed history.",
    },
    company: {
      title: "Your Company",
      slideOverDeleteMessage:
        "All related entities will be deleted. This includes company sites, campaigns, uploaded files, uploaded product feeds, plans, contracts and invoices.",
    },
    user: {
      title: "User Details",
      slideOverDeleteMessage:
        "All related data for this user will be deleted. This includes user personal data and roles assigned to this user.",
    },
    dashboard: {
      title: "Dashboard",
    },
  },
};

export const generalPageWrapperData: {
  [key in DetailPagesTypes]: {
    buttonType: ButtonType;
    linkStyle: BackLinkStyle;
    actionButtonTitle: string;
  };
} = {
  new: {
    buttonType: "primary",
    linkStyle: "cancel",
    actionButtonTitle: "Create",
  },
  detail: {
    buttonType: "danger",
    linkStyle: "go back",
    actionButtonTitle: "Delete",
  },
};
